// File: src/components/MarketingInsightsTab.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import FanFunnel from '../../../components/artistDashboard/FanFunnel';
import { useProfile } from '../../../context/ProfileContext';
import FunnelAdvice from '../../../components/artistDashboard/FunnelAdvice';
import CampaignBuilderModal from '../../../components/artistDashboard/labelServices/CampaignBuilderModal';
import HotRatersTargetModal from '../../../components/artistDashboard/labelServices/HotRatersTargetModal';
import FanLeaderboard from '../../../components/fans/FanLeaderboard';

import { BADGE_TIERS } from '../../../config/constants';

const db = getFirestore();

const MarketingInsightsTab = ({ summaryData }) => {
    const { activeProfile } = useProfile();
    const artistId = activeProfile?.id;

    const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
    const [initialGoal, setInitialGoal] = useState('get_listens');
    const [liveTierOverrides, setLiveTierOverrides] = useState(null);
    const [isHotRatersModalOpen, setIsHotRatersModalOpen] = useState(false);

    const [leaderboardData, setLeaderboardData] = useState(null);

    // 🌟 Memoized callback prevents unnecessary child re-renders
    const handleMetricsCalculated = useCallback((metrics) => {
        setLiveTierOverrides(prev => {
            if (
                prev?.uniqueTippers === metrics.uniqueTippers &&
                prev?.supaFans === metrics.supaFans &&
                prev?.ultraFans === metrics.ultraFans
            ) {
                return prev; // Skip state update if values are identical
            }
            return metrics;
        });
    }, []);

    // Single Firestore Read for fan_leaderboards/{artistId}
    useEffect(() => {
        if (!artistId) return;

        let isMounted = true;
        const fetchSingleReadFunnelData = async () => {
            try {
                const leaderboardDocRef = doc(db, 'fan_leaderboards', artistId);
                const snap = await getDoc(leaderboardDocRef);

                if (isMounted) {
                    if (snap.exists()) {
                        setLeaderboardData(snap.data());
                    } else {
                        setLeaderboardData(null);
                    }
                }
            } catch (error) {
                console.error("Error performing single-read for fan_leaderboards:", error);
            }
        };

        fetchSingleReadFunnelData();

        return () => {
            isMounted = false;
        };
    }, [artistId]);

    // Combined Dynamic Funnel Computation
    const unifiedFunnelData = useMemo(() => {
        if (!summaryData) return null;

        const fansArray = leaderboardData?.fans || [];
        let liveFans = 0;
        let liveSupaFans = 0;
        let liveUltraFans = 0;

        fansArray.forEach((fan) => {
            const points = Number(fan.overallFanScore || fan.totalPointsTipped) || 0;

            if (points >= BADGE_TIERS.FAN.minPoints) liveFans++;
            if (points >= BADGE_TIERS.SUPAFAN.minPoints) liveSupaFans++;
            if (points >= BADGE_TIERS.ULTRAFAN.minPoints) liveUltraFans++;
        });

        const finalFans = liveTierOverrides?.uniqueTippers 
            ?? (liveFans || summaryData.fans || summaryData.uniqueTippers || 0);
            
        const finalSupaFans = liveTierOverrides?.supaFans 
            ?? (liveSupaFans || summaryData.supaFans || 0);
            
        const finalUltraFans = liveTierOverrides?.ultraFans 
            ?? (liveUltraFans || summaryData.ultraFans || 0);

        return {
            uniqueHotRaters: summaryData.uniqueHotRatersCount || summaryData.uniqueHotRaters || 0,
            uniquePlaylisters: summaryData.uniquePlaylistersCount || summaryData.uniquePlaylisters || 0,
            subscribers: summaryData.subscriberCount || summaryData.subscribers || 0,
            
            uniqueTippers: finalFans,
            supaFans: finalSupaFans,
            ultraFans: finalUltraFans,
        };
    }, [summaryData, leaderboardData, liveTierOverrides]);

    const handleHotRatersActionRouting = (actionId) => {
        switch (actionId) {
            case 'publish_announcement':
                setInitialGoal('get_listens'); 
                setIsCampaignModalOpen(true);
                break;
            default:
                console.warn("Unregistered workflow action request frame:", actionId);
        }
    };

    const recommendedAction = useMemo(() => {
        if (!summaryData) return null;

        if (summaryData.uniqueHotRatersCount > 0 && (summaryData.uniquePlaylistersCount / summaryData.uniqueHotRatersCount < 0.4)) {
            return {
                text: "Listeners are rating your tracks as 'Hot' but aren't putting them in playlists. A broadcast can prompt them to anchor your music!",
                actionText: "Create a 'Playlist Add' Campaign",
                actionStage: 'uniquePlaylisters'
            };
        }

        const tippersCount = summaryData.fans || summaryData.uniqueTippersCount || 0;
        const playlistersCount = summaryData.uniquePlaylistersCount || 0;

        if (playlistersCount > 0 && (tippersCount / playlistersCount < 0.15)) {
            return {
                text: "Your music is heavily playlisted! Let's nudge your active listener pool to support your journey with a direct tip.",
                actionText: "Create a 'Tip Drive' Campaign",
                actionStage: 'uniqueTippers'
            };
        }

        return {
            text: "Keep feeding the top of your funnel. Let's push out a campaign to get more listeners rating your newest release.",
            actionText: "Boost Ratings Reach",
            actionStage: 'uniqueHotRaters'
        };
    }, [summaryData]);

    const handleStageClick = (stageKey) => {
        if (stageKey === 'uniqueHotRaters') {
            setIsHotRatersModalOpen(true);
            return;
        }

        const goalMap = {
            uniquePlaylisters: 'get_playlist_adds',
            subscribers: 'get_subscribers',
            uniqueTippers: 'get_tips',
            supaFans: 'get_tips',
            ultraFans: 'get_tips'
        };
        
        setInitialGoal(goalMap[stageKey] || 'get_listens');
        setIsCampaignModalOpen(true);
    };

    if (!summaryData) {
        return <div className="p-4 text-center">Loading your fan data...</div>;
    }

    return (
        <div className="space-y-6">
            {activeProfile?.id && (
                <div className="w-full max-w-5xl mx-auto">
                    <FanLeaderboard
                        artistId={activeProfile.id} 
                        isDashboardView={true}
                        allowSelfTipping={true}
                        hideOwner={false}   
                        showOwnerBadge={true}
                        ownerBadgeText="Hidden Publicly"
                        onMetricsCalculated={handleMetricsCalculated}
                    />
                </div>
            )}
            
            <FanFunnel 
                data={unifiedFunnelData}
                onStageClick={handleStageClick} 
            />

            {/*<FunnelAdvice 
                advice={recommendedAction} 
                onActionClick={() => handleStageClick(recommendedAction?.actionStage)} 
            />
            */}
            
            <CampaignBuilderModal
                isOpen={isCampaignModalOpen}
                onClose={() => setIsCampaignModalOpen(false)}
                artistId={artistId}
                initialGoal={initialGoal}
            />

            <HotRatersTargetModal
                isOpen={isHotRatersModalOpen}
                onClose={() => setIsHotRatersModalOpen(false)}
                onActionClick={handleHotRatersActionRouting}
            />
        </div>
    );
};

// 🌟 Memoize Component Export
export default React.memo(MarketingInsightsTab);